import type { GeoMetricRow } from '../../types/geoAnalytics'

/** |Δ| bin edges in lean points: <2, 2–5, 5–10, ≥10. */
export const SWING_BINS = [2, 5, 10] as const

export function magnitudeBin(absDelta: number): 0 | 1 | 2 | 3 {
  if (absDelta < SWING_BINS[0]) return 0
  if (absDelta < SWING_BINS[1]) return 1
  if (absDelta < SWING_BINS[2]) return 2
  return 3
}

export function formatSwing(delta: number): string {
  const rounded = Math.round(delta * 10) / 10
  if (Math.abs(rounded) < 0.05) return 'No shift'
  return rounded > 0 ? `D+${rounded.toFixed(1)} shift` : `R+${Math.abs(rounded).toFixed(1)} shift`
}

/**
 * NYT-style swing arrows: one Point per unit at its server-provided interior
 * centroid. D-shift → blue, up-right (+45°); R-shift → red, up-left (−45°).
 * Units missing either cycle, with a negligible shift, or without a centroid
 * are omitted (spec §3.3).
 */
export function buildSwingGeoJson(
  units: GeoJSON.FeatureCollection,
  fromRows: GeoMetricRow[],
  toRows: GeoMetricRow[],
): GeoJSON.FeatureCollection {
  const fromBy = new Map(fromRows.map(r => [r.geoUnitId, r.value]))
  const toBy = new Map(toRows.map(r => [r.geoUnitId, r.value]))

  const features: GeoJSON.Feature[] = []
  for (const feature of units.features) {
    const id = feature.properties?.id as string | undefined
    const centroid = feature.properties?.centroid as [number, number] | undefined
    if (!id || !centroid || centroid.length !== 2) continue

    const from = fromBy.get(id)
    const to = toBy.get(id)
    if (from == null || to == null) continue

    const delta = to - from
    if (Math.abs(delta) < 0.05) continue

    const direction = delta > 0 ? 'D' : 'R'
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [centroid[0], centroid[1]] },
      properties: {
        id,
        name: (feature.properties?.name as string) ?? '',
        direction,
        rotation: direction === 'D' ? 45 : -45,
        sizeBin: magnitudeBin(Math.abs(delta)),
        displayValue: `${(feature.properties?.name as string) ?? id} — ${formatSwing(delta)}`,
      },
    })
  }
  return { type: 'FeatureCollection', features }
}
